import { useState } from "react";
import Button from "../ui/Button.jsx";
import {
  mapAuth0ConnectApiError,
  validateAuth0ConnectFields,
} from "./auth0ConnectValidation.js";
import { AUTH0_M2M_GUIDANCE } from "./auth0ConnectGuidance.js";
import Auth0ScopeStatusList from "./Auth0ScopeStatusList.jsx";
import { AUTH0_REQUIRED_SCOPE_COUNT } from "./auth0ScopeConfig.js";
import ProviderFrameworkPicker, { normalizeFrameworksForProviderSubmit } from "./ProviderFrameworkPicker.jsx";
import { getDefaultProviderComplianceFrameworks } from "../../utils/providerFrameworks.js";

/**
 * New-project Auth0 flow — mirrors ConnectEntraPanel (AUTH0.IMPL.3-rev).
 *
 * @param {{ onBack: () => void, onConnected: (projectId: string) => void }} props
 */
export default function ConnectAuth0Panel({ onBack, onConnected }) {
  const [tenantDomain, setTenantDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [testOk, setTestOk] = useState(false);
  const [scopesGranted, setScopesGranted] = useState(/** @type {string[]} */ ([]));
  const [scopeStatus, setScopeStatus] = useState(
    /** @type {{ scope: string, granted: boolean, tier: string }[]} */ ([]),
  );
  const [scopeWarning, setScopeWarning] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [frameworks, setFrameworks] = useState(() => getDefaultProviderComplianceFrameworks("auth0"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTest = async () => {
    setError(null);
    setTestOk(false);
    setScopesGranted([]);
    setScopeStatus([]);
    setScopeWarning("");
    const validated = validateAuth0ConnectFields({
      tenant_domain: tenantDomain,
      client_id: clientId,
      client_secret: clientSecret,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/auth0/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_domain: validated.tenant_domain,
          client_id: validated.client_id,
          client_secret: validated.client_secret,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        const missing = Array.isArray(data.scopes_missing_required)
          ? data.scopes_missing_required
          : Array.isArray(data.scopes_missing)
            ? data.scopes_missing
            : [];
        if (missing.length > 0) {
          setError(`${data.error || "Required scopes missing"}: ${missing.join(", ")}`);
        } else {
          setError(mapAuth0ConnectApiError(data));
        }
        return;
      }
      setTestOk(true);
      setScopesGranted(Array.isArray(data.scopes_granted) ? data.scopes_granted : []);
      setScopeStatus(Array.isArray(data.scope_status) ? data.scope_status : []);
      setScopeWarning(typeof data.scope_warning === "string" ? data.scope_warning : "");
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testOk) return;
    setError(null);
    const validated = validateAuth0ConnectFields({
      tenant_domain: tenantDomain,
      client_id: clientId,
      client_secret: clientSecret,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/projects/auth0", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_domain: validated.tenant_domain,
          client_id: validated.client_id,
          client_secret: validated.client_secret,
          display_name: displayName.trim() || undefined,
          name: displayName.trim() || undefined,
          complianceFrameworks: normalizeFrameworksForProviderSubmit("auth0", frameworks),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.project?.projectId) {
        throw new Error(mapAuth0ConnectApiError(data));
      }
      const projectId = data.project.projectId;
      try {
        sessionStorage.setItem("idwiz.activeProjectId", projectId);
      } catch {
        /* ignore */
      }
      onConnected?.(projectId);
    } catch (err) {
      setError(err?.message || "Could not create project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 32px 8px" }}>
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
          {AUTH0_M2M_GUIDANCE}
        </p>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Project name (optional)
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Auth0 assessment"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Tenant domain
          </span>
          <input
            required
            value={tenantDomain}
            onChange={(e) => {
              setTenantDomain(e.target.value);
              setTestOk(false);
            }}
            placeholder="your-tenant.us.auth0.com"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            M2M Client ID
          </span>
          <input
            required
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setTestOk(false);
            }}
            autoComplete="off"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            M2M Client Secret
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              required
              type={showSecret ? "text" : "password"}
              value={clientSecret}
              onChange={(e) => {
                setClientSecret(e.target.value);
                setTestOk(false);
              }}
              autoComplete="off"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={() => setShowSecret((s) => !s)} style={toggleBtnStyle}>
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        <div style={{ marginBottom: "16px" }}>
          <div className="text-secondary" style={{ fontSize: "12px", marginBottom: "8px" }}>
            Compliance frameworks
          </div>
          <ProviderFrameworkPicker providerId="auth0" value={frameworks} onChange={setFrameworks} />
        </div>

        {testOk ? (
          <>
            <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
              ✓ Connection verified — {scopesGranted.length} scope{scopesGranted.length === 1 ? "" : "s"} granted
              {scopesGranted.length >= AUTH0_REQUIRED_SCOPE_COUNT ? " (all required scopes OK)" : ""}
            </p>
            <Auth0ScopeStatusList granted={scopesGranted} scopeStatus={scopeStatus} compact />
            {scopeWarning ? (
              <p style={{ fontSize: "12px", color: "var(--warning)", marginTop: "8px" }}>{scopeWarning}</p>
            ) : null}
          </>
        ) : null}

        {error ? (
          <p className="text-critical" style={{ fontSize: "13px" }}>
            {error}
          </p>
        ) : null}
      </div>

      <div style={footerStyle}>
        {!testOk && !testBusy ? (
          <span className="text-secondary" style={{ fontSize: "11px", marginRight: "auto" }}>
            Test connection before creating the project.
          </span>
        ) : null}
        <Button type="button" variant="ghost" onClick={onBack} disabled={loading || testBusy}>
          Back
        </Button>
        <Button type="button" variant="secondary" disabled={testBusy || loading} onClick={() => void handleTest()}>
          {testBusy ? "Testing…" : "Test Connection"}
        </Button>
        <Button type="submit" variant="primary" loading={loading} disabled={loading || !testOk}>
          {loading ? "Creating…" : "Create Project"}
        </Button>
      </div>
    </form>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "4px",
  border: "1px solid var(--border)",
  background: "var(--bg-input)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const toggleBtnStyle = {
  background: "var(--bg-elevated)",
  border: "1px solid var(--border)",
  borderRadius: "4px",
  padding: "0 12px",
  cursor: "pointer",
  color: "var(--text-secondary)",
};

const footerStyle = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 32px 24px",
  borderTop: "1px solid var(--border)",
};
